/**
 * Author typeahead of the one-off ISBN/ISMN registration (DB-804): an author is either picked from the NK
 * authorities (linked by the person id, names taken from the authority) or typed in as a new author.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/** Open route of mod-br-agency serving the typeahead to the anonymous registration form. */
export var AUTHORITY_AUTHORS_PATH = "agency/registration/authority-authors";
/** Shortest query the typeahead searches for; the backend enforces the same minimum. */
export var AUTHORITY_AUTHOR_MIN_QUERY_LENGTH = 3;
/** Most options the typeahead asks for; the backend caps the limit at the same value. */
export var AUTHORITY_AUTHOR_LIMIT = 20;
/** Longest part of the biographical note shown in a typeahead option, the same as in the ReČeK GUI. */
export var AUTHORITY_AUTHOR_NOTE_MAX_LENGTH = 60;
// NK headings mark the non-sorting part as "<<z >>Lobkowicz"; show it as "z Lobkowicz"
var stripNonSortingMarkers = function (value) {
    return (value !== null && value !== void 0 ? value : "").replace(/<<|>>/g, "").replace(/\s+/g, " ").trim();
};
var truncateNote = function (note) {
    return note.length > AUTHORITY_AUTHOR_NOTE_MAX_LENGTH
        ? "".concat(note.slice(0, AUTHORITY_AUTHOR_NOTE_MAX_LENGTH).trimEnd(), "\u2026")
        : note;
};
/**
 * @param author an authority or an author row
 * @returns its trimmed biographical note, an empty string when it has none
 */
export var authorityBiographicalNote = function (author) { var _a, _b; return (_b = (_a = author === null || author === void 0 ? void 0 : author.biographicalNote) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : ""; };
// dates are stored as YYYY, MM.YYYY or DD.MM.YYYY
var yearOf = function (date) { var _a, _b; return (_b = (_a = date === null || date === void 0 ? void 0 : date.trim().match(/(\d{3,4})$/)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : ""; };
/**
 * Typeahead option label: "Příjmení, Jméno (narození–úmrtí) — biografická poznámka [ID AUT]"; the note is cut to
 * {@link AUTHORITY_AUTHOR_NOTE_MAX_LENGTH} characters with "…" like in the ReČeK GUI, parts that are missing are left
 * out.
 *
 * @param author the authority
 * @returns the label
 */
export var buildAuthorityAuthorLabel = function (author) {
    var _a;
    var name = [
        stripNonSortingMarkers(author.lastName),
        stripNonSortingMarkers(author.firstName),
    ]
        .filter(Boolean)
        .join(", ");
    var birth = yearOf(author.birthDate);
    var death = yearOf(author.deathDate);
    var years = birth || death ? " (".concat(birth, "\u2013").concat(death, ")") : "";
    var note = authorityBiographicalNote(author);
    var noteSuffix = note ? " \u2014 ".concat(truncateNote(note)) : "";
    var sourceId = (_a = author.sourceId) === null || _a === void 0 ? void 0 : _a.trim();
    return "".concat(name).concat(years).concat(noteSuffix).concat(sourceId ? " [".concat(sourceId, "]") : "");
};
/**
 * @param query the text typed into the typeahead
 * @returns whether the query is long enough to be searched
 */
export var isAuthorityAuthorQuery = function (query) {
    return (query !== null && query !== void 0 ? query : "").trim().length >= AUTHORITY_AUTHOR_MIN_QUERY_LENGTH;
};
/**
 * The author row for a picked authority: names come from the authority, the row is linked to it and keeps the
 * authority's biographical note for display.
 *
 * @param author the picked authority
 * @returns the linked author row
 */
export var authorFromAuthority = function (author) {
    var _a, _b;
    var biographicalNote = authorityBiographicalNote(author);
    return __assign({ firstName: stripNonSortingMarkers(author.firstName), lastName: stripNonSortingMarkers(author.lastName), personId: author.id, sourceId: (_b = (_a = author.sourceId) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : "" }, (biographicalNote && { biographicalNote: biographicalNote }));
};
/** An empty, unlinked author row ("Zrušit výběr" and "Přidat autora"). */
export var unlinkedAuthor = function () { return ({
    firstName: "",
    lastName: "",
    personId: "",
    sourceId: "",
}); };
/**
 * @param author an author row
 * @returns whether the row is linked to an NK authority (its names are then read-only)
 */
export var isLinkedAuthor = function (author) { var _a; return !!((_a = author === null || author === void 0 ? void 0 : author.personId) === null || _a === void 0 ? void 0 : _a.trim()); };
/**
 * Author rows as sent to the backend: rows without any name are dropped, names are trimmed and the NK link is
 * kept only for a row linked to a picked person (a blank ID AUT is left out). The display-only biographical note is
 * never sent.
 *
 * @param authors the form rows
 * @returns the authors to submit
 */
export var normalizeAuthors = function (authors) {
    return (authors !== null && authors !== void 0 ? authors : [])
        .map(function (author) {
        var _a, _b, _c, _d, _e, _f;
        var personId = (_a = author.personId) === null || _a === void 0 ? void 0 : _a.trim();
        var sourceId = (_b = author.sourceId) === null || _b === void 0 ? void 0 : _b.trim();
        return __assign(__assign({ firstName: (_d = (_c = author.firstName) === null || _c === void 0 ? void 0 : _c.trim()) !== null && _d !== void 0 ? _d : "", lastName: (_f = (_e = author.lastName) === null || _e === void 0 ? void 0 : _e.trim()) !== null && _f !== void 0 ? _f : "" }, (personId && { personId: personId })), (personId && sourceId && { sourceId: sourceId }));
    })
        .filter(function (author) { return author.firstName || author.lastName; });
};
/**
 * Persons linked in the author rows other than the given one; the row must not offer them again, so one authority
 * is never linked twice in a work specification (DB-804).
 *
 * @param authors all author rows
 * @param index the row asking
 * @returns the person ids linked in the other rows
 */
export var personIdsLinkedElsewhere = function (authors, index) {
    return (authors !== null && authors !== void 0 ? authors : [])
        .filter(function (_, rowIndex) { return rowIndex !== index; })
        .map(function (author) { var _a, _b; return (_b = (_a = author === null || author === void 0 ? void 0 : author.personId) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : ""; })
        .filter(Boolean);
};
